import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Building2, Users, CreditCard, Package, Key, Rocket,
  Gauge, ScrollText, GitCompare, Clock, Search as SearchIcon, ShieldCheck } from 'lucide-react';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandShortcut,
} from '@/components/ui/command';
import { useAdminAuth } from '../../modules/admin/auth/AdminAuthContext';
import { permissionForPath } from '../../modules/admin/navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

// Icons and search keywords are the palette's own presentation concern; which
// permission each destination needs is not, and is read from the shared
// navigation registry below. This list used to be permission-blind, so it
// offered every screen — including Create Tenant — to every operator.
const NAV_ITEMS = [
  { label: 'Dashboard', path: '/', icon: LayoutDashboard, keywords: 'control plane home overview' },
  { label: 'Tenants', path: '/tenants', icon: Building2 },
  { label: 'Create Tenant', path: '/tenants/create', icon: Building2 },
  { label: 'Users', path: '/users', icon: Users },
  { label: 'Plans & Billing', path: '/subscriptions', icon: CreditCard, keywords: 'subscription plans mrr' },
  { label: 'Products', path: '/modules', icon: Package, keywords: 'modules entitlements' },
  { label: 'License Catalog', path: '/license-catalog', icon: Key },
  { label: 'Releases', path: '/releases', icon: Rocket },
  { label: 'Quota Catalog', path: '/quotas', icon: Gauge, keywords: 'limits' },
  { label: 'Audit Logs', path: '/audit-logs', icon: ScrollText },
  { label: 'Platform Drift', path: '/platform-drift', icon: GitCompare, keywords: 'legacy canonical' },
  { label: 'Team Access', path: '/operators', icon: ShieldCheck, keywords: 'operators admins access roles owner team' },
];

const RECENTS_KEY = 'cluvik-admin-recent-pages';
const MAX_RECENTS = 5;

export const pushRecentPage = (item) => {
  try {
    const existing = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]').filter((r) => r.path !== item.path);
    const next = [item, ...existing].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable -- recents are a convenience, fail silently
  }
};

const getRecentPages = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
  } catch {
    return [];
  }
};

/**
 * Ctrl+K / Cmd+K command palette. Navigation items are static routes
 * already registered in AdminRoutes. Entity search hits the same
 * search-capable endpoints (`/admin/tenants?search=`, `/admin/users?search=`,
 * `/admin/license-catalog?search=`) the Tenants/Users/License Catalog pages
 * already call -- no new backend surface.
 */
const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ tenants: [], users: [], licenses: [] });
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const { getAdminToken, can } = useAdminAuth();

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults({ tenants: [], users: [], licenses: [] });
      return;
    }
    setSearching(true);
    const token = getAdminToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [tenantsRes, usersRes, licensesRes] = await Promise.allSettled([
        axios.get(`${ADMIN_API}/tenants`, { headers, params: { search: q, limit: 5 } }),
        axios.get(`${ADMIN_API}/users`, { headers, params: { search: q, limit: 5 } }),
        axios.get(`${ADMIN_API}/license-catalog`, { headers, params: { search: q } }),
      ]);
      setResults({
        tenants: tenantsRes.status === 'fulfilled' ? (tenantsRes.value.data.tenants || []).slice(0, 5) : [],
        users: usersRes.status === 'fulfilled' ? (usersRes.value.data.users || []).slice(0, 5) : [],
        licenses: licensesRes.status === 'fulfilled' ? (licensesRes.value.data.licenses || []).slice(0, 5) : [],
      });
    } finally {
      setSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  // A destination is offered only if the backend says this operator holds the
  // permission that screen needs. An unknown path is dropped rather than shown,
  // so adding a screen to the palette without registering it fails closed.
  const permitted = useCallback((path) => {
    const screen = permissionForPath(path);
    return !!screen && can(screen.permission);
  }, [can]);

  const allowedNavItems = useMemo(
    () => NAV_ITEMS.filter((i) => permitted(i.path)),
    [permitted],
  );

  // Recents come from localStorage and survive a permission change, so they are
  // filtered on the way out, not on the way in.
  const recents = useMemo(
    () => (open ? getRecentPages().filter((r) => permitted(r.path)) : []),
    [open, permitted],
  );

  const go = (path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  const hasEntityResults = results.tenants.length || results.users.length || results.licenses.length;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search tenants, users, licenses, or jump to a page..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {searching ? 'Searching...' : 'No results found.'}
        </CommandEmpty>

        {!query && recents.length > 0 && (
          <CommandGroup heading="Recently visited">
            {recents.map((r) => (
              <CommandItem key={r.path} value={`recent-${r.label}`} onSelect={() => go(r.path)}>
                <Clock className="h-4 w-4 text-muted-foreground" />
                {r.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {!query && (
          <CommandGroup heading="Navigate">
            {allowedNavItems.map((item) => (
              <CommandItem
                key={item.path}
                value={`${item.label} ${item.keywords || ''}`}
                onSelect={() => go(item.path)}
              >
                <item.icon className="h-4 w-4 text-muted-foreground" />
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {query && (
          <CommandGroup heading="Pages">
            {allowedNavItems.filter((i) => (i.label + ' ' + (i.keywords || '')).toLowerCase().includes(query.toLowerCase())).map(
              (item) => (
                <CommandItem key={item.path} value={`${item.label} ${item.keywords || ''}`} onSelect={() => go(item.path)}>
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  {item.label}
                </CommandItem>
              )
            )}
          </CommandGroup>
        )}

        {query.trim().length >= 2 && hasEntityResults > 0 && (
          <>
            {results.tenants.length > 0 && (
              <CommandGroup heading="Tenants">
                {results.tenants.map((t) => (
                  <CommandItem key={t.id} value={`tenant-${t.id}`} onSelect={() => go(`/tenants/${t.id}`)}>
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    {t.organization_name || t.tenant_name || t.company_name || t.id}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.users.length > 0 && (
              <CommandGroup heading="Users">
                {results.users.map((u) => (
                  <CommandItem key={u.id} value={`user-${u.id}`} onSelect={() => go('/users')}>
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {u.email || u.name || u.id}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.licenses.length > 0 && (
              <CommandGroup heading="Licenses">
                {results.licenses.map((l) => (
                  <CommandItem key={l.id} value={`license-${l.id}`} onSelect={() => go('/license-catalog')}>
                    <Key className="h-4 w-4 text-muted-foreground" />
                    {l.license_name} <CommandShortcut>{l.license_code}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export const CommandPaletteTrigger = ({ className }) => {
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  return (
    <button
      onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: !isMac, metaKey: isMac }))}
      className={className}
      type="button"
    >
      <SearchIcon className="h-3.5 w-3.5" />
      <span>Search everything</span>
      <kbd className="ml-auto rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
        {isMac ? '⌘K' : 'Ctrl K'}
      </kbd>
    </button>
  );
};

export default CommandPalette;
